/**
 * Fleet Issues Card — compact summary of action items and alerts.
 * Links to Analytics → Action Items section.
 * Tier 1 card (always visible).
 */

import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import type { FleetSummaryCounts } from '@/lib/fleet-severity';
import type { NotificationActivityRow } from '@/hooks/useNotificationActivity';

interface FleetIssuesCardProps {
  counts: FleetSummaryCounts;
  events: NotificationActivityRow[];
}

export function FleetIssuesCard({ counts, events }: FleetIssuesCardProps) {
  const navigate = useNavigate();

  const actionItems = counts.critical + counts.watch;
  const sentToday = events.filter((e) => e.outcome === 'sent').length;
  const resolvedToday = events.filter((e) => e.outcome === 'resolved').length;

  return (
    <div className="flex-1 min-w-[200px] rounded-lg border border-border bg-card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">Issues</span>
        <AlertTriangle className="h-5 w-5 text-wt-amber-primary" />
      </div>
      <div className="space-y-2">
        <p className={`font-mono text-[28px] font-semibold leading-tight ${actionItems > 0 ? 'text-wt-amber-primary' : 'text-wt-green-primary'}`}>
          {actionItems > 0 ? `${actionItems} action items` : 'No action items'}
        </p>
        <div className="space-y-1 text-sm text-muted-foreground">
          <button
            type="button"
            onClick={() => navigate('/app/alerts?date=today&outcome=sent')}
            className="hover:text-foreground hover:underline transition-colors"
          >
            {sentToday} alerts sent today
          </button>
          <p>{resolvedToday} resolved</p>
        </div>
      </div>
      <button
        type="button"
        onClick={() => navigate('/app/analytics#action-items')}
        className="flex items-center gap-1.5 text-xs font-mono text-primary hover:underline transition-colors"
      >
        View Actions
        <ArrowRight className="h-3 w-3" />
      </button>
    </div>
  );
}
